/**
 * Soracom Wizard - Centralized Theme Configuration
 * 
 * This file contains all color, spacing, typography, and design tokens.
 * Update this file when rebranding to apply changes across the entire application.
 * 
 * Version: 1.0
 * Last Updated: May 2026
 */

// ============================================================================
// COLOR PALETTE
// ============================================================================

export const colors = {
  // Primary Brand Colors
  primary: {
    main: '#1976d2',      // Main brand color
    dark: '#1565c0',      // Hover states, emphasis
    light: '#e3f2fd',     // Backgrounds, subtle highlights
    contrast: '#ffffff',  // Text on primary buttons
  },

  // Neutral Colors
  neutral: {
    darkText: '#2c3e50',       // Primary text
    mediumText: '#7f8c8d',     // Secondary text, subtitles
    lightText: '#95a5a6',      // Hints, captions
    border: 'rgba(0,0,0,0.06)', // Dividers, card borders
    background: '#f5f5f5',     // Page background
    white: '#ffffff',          // Cards, surfaces
    gray100: '#f5f5f5',
    gray200: '#e0e0e0',
    gray300: '#bdbdbd',
    gray400: '#9e9e9e',
  },

  // Status Colors
  status: {
    success: {
      main: '#4caf50',
      light: '#e8f5e9',
      dark: '#2e7d32',
    },
    error: {
      main: '#f44336',
      light: '#ffebee',
      dark: '#c62828',
    },
    warning: {
      main: '#ff9800',
      light: '#fff3e0',
      dark: '#e65100',
    },
    info: {
      main: '#1976d2',
      light: '#e3f2fd',
      dark: '#1565c0',
    },
  },

  // Special Backgrounds
  backgrounds: {
    dark: '#0F1837',           // Login page background
    overlay: 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)',
    sidebar: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
};

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

  fontSize: {
    h1: '32px',
    h2: '28px',
    h3: '24px',
    h4: '20px',
    h5: '18px',
    h6: '16px',
    bodyLarge: '16px',
    bodyRegular: '14px',
    bodySmall: '13px',
    caption: '12px',
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    heading: 1.2,
    body: 1.5,
    button: 1.75,
  },
};

// ============================================================================
// SPACING
// ============================================================================

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
};

// Helper function to multiply spacing
export const getSpacing = (multiplier = 1) => `${4 * multiplier}px`;

// ============================================================================
// BORDER RADIUS
// ============================================================================

export const borderRadius = {
  small: '8px',
  medium: '12px',
  large: '16px',
  round: '50%',
  pill: '24px',
};

// ============================================================================
// SHADOWS
// ============================================================================

export const shadows = {
  none: 'none',
  small: '0 2px 4px rgba(0, 0, 0, 0.04)',
  medium: '0 2px 8px rgba(0, 0, 0, 0.08)',
  large: '0 4px 12px rgba(0, 0, 0, 0.1)',
  elevated: '0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
  button: '0 2px 4px rgba(25, 118, 210, 0.2)',
  buttonHover: '0 4px 12px rgba(25, 118, 210, 0.3)',
};

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transitions = {
  default: 'all 0.2s ease',
  button: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  modal: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  hover: 'transform 0.2s ease',
};

// ============================================================================
// Z-INDEX LAYERS
// ============================================================================

export const zIndex = {
  base: 1,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ============================================================================
// BREAKPOINTS
// ============================================================================

export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1920px',
};

// Media query helpers
export const mediaQueries = {
  xs: `@media (min-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm})`,
  md: `@media (min-width: ${breakpoints.md})`,
  lg: `@media (min-width: ${breakpoints.lg})`,
  xl: `@media (min-width: ${breakpoints.xl})`,
};

// ============================================================================
// COMPONENT SPECIFIC STYLES
// ============================================================================

export const components = {
  // Cards
  card: {
    background: colors.neutral.white,
    borderRadius: borderRadius.medium,
    shadow: shadows.medium,
    elevatedShadow: shadows.elevated,
    border: `1px solid ${colors.neutral.border}`,
    padding: spacing.lg,
    paddingLarge: spacing.xl,
  },

  // Buttons
  button: {
    borderRadius: borderRadius.small,
    padding: '10px 24px',
    fontWeight: typography.fontWeight.semibold,
    fontSize: typography.fontSize.bodyRegular,
    transition: transitions.button,
    shadow: shadows.button,
    hoverShadow: shadows.buttonHover,
  },

  // Input Fields
  input: {
    border: `1px solid ${colors.neutral.gray200}`,
    borderRadius: borderRadius.small,
    padding: '12px 16px',
    background: colors.neutral.white,
    focusBorder: `2px solid ${colors.primary.main}`,
    errorBorder: `2px solid ${colors.status.error.main}`,
    labelColor: colors.neutral.mediumText,
    iconColor: colors.neutral.gray400,
    placeholderColor: colors.neutral.gray300,
  },

  // Sidebar
  sidebar: {
    width: '260px',
    background: colors.backgrounds.sidebar,
    textColor: 'rgba(255, 255, 255, 0.9)',
    itemHeight: '48px',
    itemBorderRadius: borderRadius.small,
    iconSize: '22px',
    hoverBackground: 'rgba(255, 255, 255, 0.08)',
    activeBackground: 'linear-gradient(90deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
  },

  // Navbar
  navbar: {
    height: '64px',
    background: colors.neutral.white,
    borderBottom: `1px solid ${colors.neutral.border}`,
    shadow: shadows.small,
  },

  // Tables
  table: {
    headerBackground: colors.neutral.gray100,
    border: `1px solid ${colors.neutral.gray200}`,
    rowHover: `rgba(25, 118, 210, 0.04)`,
    cellPadding: '12px 16px',
    fontSize: typography.fontSize.bodyRegular,
  },

  // Badges/Chips
  badge: {
    height: {
      small: '24px',
      medium: '32px',
    },
    borderRadius: borderRadius.pill,
    padding: '0 12px',
    fontSize: {
      small: typography.fontSize.caption,
      medium: typography.fontSize.bodyRegular,
    },
    fontWeight: typography.fontWeight.medium,
  },

  // Notifications/Snackbar
  snackbar: {
    minWidth: '300px',
    maxWidth: '450px',
    borderRadius: borderRadius.medium,
    padding: '16px 20px',
    shadow: shadows.elevated,
    borderLeftWidth: '4px',
    iconSize: '24px',
  },

  // Tabs
  tabs: {
    height: '48px',
    indicatorColor: colors.primary.main,
    indicatorHeight: '3px',
    activeColor: colors.primary.main,
    inactiveColor: colors.neutral.mediumText,
    hoverBackground: `rgba(25, 118, 210, 0.04)`,
  },
};

// ============================================================================
// MATERIAL-UI THEME INTEGRATION
// ============================================================================

export const getMuiTheme = () => ({
  palette: {
    primary: {
      main: colors.primary.main,
      dark: colors.primary.dark,
      light: colors.primary.light,
      contrastText: colors.primary.contrast,
    },
    secondary: {
      main: colors.status.success.main,
      dark: colors.status.success.dark,
      light: colors.status.success.light,
    },
    error: {
      main: colors.status.error.main,
      dark: colors.status.error.dark,
      light: colors.status.error.light,
    },
    warning: {
      main: colors.status.warning.main,
      dark: colors.status.warning.dark,
      light: colors.status.warning.light,
    },
    info: {
      main: colors.status.info.main,
      dark: colors.status.info.dark,
      light: colors.status.info.light,
    },
    background: {
      default: colors.neutral.background,
      paper: colors.neutral.white,
    },
    text: {
      primary: colors.neutral.darkText,
      secondary: colors.neutral.mediumText,
    },
  },
  typography: {
    fontFamily: typography.fontFamily,
    h1: {
      fontSize: typography.fontSize.h1,
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.heading,
    },
    h2: {
      fontSize: typography.fontSize.h2,
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.heading,
    },
    h3: {
      fontSize: typography.fontSize.h3,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.heading,
    },
    h4: {
      fontSize: typography.fontSize.h4,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.heading,
    },
    h5: {
      fontSize: typography.fontSize.h5,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.heading,
    },
    h6: {
      fontSize: typography.fontSize.h6,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.heading,
    },
    body1: {
      fontSize: typography.fontSize.bodyRegular,
      lineHeight: typography.lineHeight.body,
    },
    body2: {
      fontSize: typography.fontSize.bodySmall,
      lineHeight: typography.lineHeight.body,
    },
    button: {
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'none',
      fontSize: typography.fontSize.bodyRegular,
    },
    caption: {
      fontSize: typography.fontSize.caption,
      lineHeight: typography.lineHeight.body,
    },
  },
  shape: {
    borderRadius: parseInt(borderRadius.medium),
  },
  shadows: [
    shadows.none,
    shadows.small,
    shadows.medium,
    shadows.large,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
    shadows.elevated,
  ],
});

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const theme = {
  colors,
  typography,
  spacing,
  getSpacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  mediaQueries,
  components,
  getMuiTheme,
};

export default theme;
